/**
 * Canonical intake status / document classification enums.
 *
 * Declared here as `const` objects so we get both the runtime values (used
 * across stores, routes, and components) AND literal-type unions for use in
 * typed code. `intake.js` re-exports these for backwards compatibility so
 * existing callers keep working.
 */
export const INTAKE_STAGE = {
    INTAKE: "INTAKE",
    QUALIFY_PATIENT: "QUALIFY_PATIENT",
    CLINICAL_REVIEW: "CLINICAL_REVIEW",
    PRIOR_AUTHORIZATION: "PRIOR_AUTHORIZATION",
    DENIAL_FOLLOWUP: "DENIAL_FOLLOWUP",
    APPROVED: "APPROVED",
    ARCHIVED: "ARCHIVED",
};
export const DOCUMENT_STAGE = {
    PRE_PROCESSING: "PRE_PROCESSING",
    AUTOSUBMIT_CHECK: "AUTOSUBMIT_CHECK",
    INTAKE: "INTAKE",
    ARCHIVED: "ARCHIVED",
    EHR_PROCESSING: "EHR_PROCESSING",
    SENT_TO_EHR: "SENT_TO_EHR",
    INTAKE_COMPLETE: "INTAKE_COMPLETE",
    OCR_PROCESSED: "OCR_PROCESSED",
    OCR_FAILED: "OCR_FAILED",
};
export const DOCUMENT_CLASSIFICATIONS = {
    INSURANCE_CARD: "INSURANCE_CARD",
    ORDER_FORM: "ORDER_FORM",
    OTHER: "OTHER",
    PAYOR_RESPONSE: "PAYOR_RESPONSE",
    APPROVAL: "APPROVAL",
    DENIAL: "DENIAL",
    TO_DO: "TO_DO",
};
// Canonical status → { label, color } for Intake — the single source the table
// chips, the Settings → Action Needed toggles, and the audit-log status lines all
// read from (spans document + order stages, since the combined feed carries either).
export const INTAKE_STATUS_MAP = {
    PRE_PROCESSING: { label: "Pre-Processing", color: "warning" },
    OCR_PROCESSED: { label: "OCR Complete", color: "warning" },
    AUTOSUBMIT_CHECK: { label: "OCR Complete", color: "warning" },
    OCR_FAILED: { label: "Pre-Processing", color: "warning" },
    INTAKE: { label: "Document Review", color: "info" },
    EHR_PROCESSING: { label: "EHR Processing", color: "warning" },
    SENT_TO_EHR: { label: "Sent to EHR", color: "success" },
    INTAKE_COMPLETE: { label: "Completed", color: "success" },
    ARCHIVED: { label: "Archived", color: "secondary" },
    QUALIFY_PATIENT: { label: "Benefit Verification", color: "info" },
    CLINICAL_REVIEW: { label: "Medical Necessity", color: "info" },
    PRIOR_AUTHORIZATION: { label: "Ready for PA", color: "success" },
    DENIAL_FOLLOWUP: { label: "Denial Follow-up", color: "error" },
    APPROVED: { label: "Approved", color: "success" },
};
export const INTAKE_STATUS_LABEL_BY_KEY = Object.fromEntries(Object.entries(INTAKE_STATUS_MAP).map(([key, { label }]) => [key, label]));
// Resolve any status key to a label. Humanizes deprecated / unknown keys (e.g. the
// deprecated DISCONTINUE_PROCESSING) so audit rows of old statuses never render blank.
export function intakeStatusLabel(status) {
    if (!status)
        return "";
    return (INTAKE_STATUS_LABEL_BY_KEY[status] ??
        status
            .toLowerCase()
            .split("_")
            .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
            .join(" "));
}
// "Completed" is a composite filter spanning both terminal "done" statuses, so
// no separate "Sent to EHR" option is needed. SENT_TO_EHR remains a real backend
// status (sync path, analytics, badge display) — this only trims the dropdown.
export const COMPLETED_STATUS_FILTER = "INTAKE_COMPLETE,SENT_TO_EHR";
// UI-only pseudo-status: a server-side snooze filter, not a status to union with.
export const SNOOZED_STATUS_FILTER = "__snoozed";
// Flat list, no section subheaders (they read like selectable statuses).
// "Ready for PA" is filterable for parity with the Action Needed settings and
// row chips (LAM-4305). DENIAL_FOLLOWUP / APPROVED are intentionally absent —
// no live code path sets them.
export const INTAKE_STATUS_FILTER_OPTIONS = [
    { title: "All Active", value: "" },
    { title: "Document Review", value: "INTAKE" },
    { title: "Benefit Verification", value: "QUALIFY_PATIENT" },
    { title: "Medical Necessity", value: "CLINICAL_REVIEW" },
    { title: "Ready for PA", value: "PRIOR_AUTHORIZATION" },
    { title: "Completed", value: COMPLETED_STATUS_FILTER },
    { title: "Pre-Processing", value: "PRE_PROCESSING" },
    { title: "OCR Complete", value: "OCR_PROCESSED,AUTOSUBMIT_CHECK" },
    { title: "EHR Processing", value: "EHR_PROCESSING" },
    { title: "Archived", value: "ARCHIVED" },
    { title: "Snoozed", value: SNOOZED_STATUS_FILTER, exclusive: true },
];
